import { PTextFieldWrapper } from '@porsche-design-system/components-react';
import styled, { css } from 'styled-components';
import breakpoints from '../../../utilities/breakpoints';
import themeTokens from '../themeTokens';

const DISABLED_COLOR = 'rgb(148, 149, 152) !important;';
const DISABLED_BORDER_COLOR_BOX = '#949598 !important';

export const formItemCss = css`
    & .ant-form-item-label {
        & > label {
            line-height: ${themeTokens.fontLineHeight};
            font-size: ${themeTokens.baseFontSize};
        }

        & > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
            display: none;
        }

        & > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
            display: inline-block;
            margin-right: 4px;
            font-size: 1rem;
            font-family: SimSun, sans-serif;
            line-height: 1;
            content: '*';
            color: rgb(1, 2, 5);
        }
    }

    & .ant-form-item-explain-error {
        color: rgb(204, 25, 34);
        margin-top: 0.25rem;
        margin-bottom: 24px;

        &:empty {
            /* When error shown but there is no tag inside */
            margin-top: 0;
        }

        & .form-item-error-container {
            display: flex;
            align-items: center;
        }

        & .form-item-error-icon {
            margin-right: 4px;
        }

        & .form-item-error-message {
            display: inline-flex;
            font-size: 1rem;
            line-height: 24px;
        }
    }
`;

export const formItemDisabledCss = css`
    & .ant-form-item-label {
        & > label {
            color: ${DISABLED_COLOR};
        }

        & > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
            color: ${DISABLED_COLOR};
        }
    }
`;

const commonPicker = css`
    ${formItemCss}

    & .ant-picker {
        height: 54px !important;
        padding: 8px 16px !important;
        border: 2px solid rgb(107, 109, 112) !important;
        border-radius: 4px !important;
        outline: 0px !important;

        & input {
            font-size: 1rem !important;
            line-height: 24px;
            color: rgb(1, 2, 5);

            &::placeholder {
                color: rgb(1, 2, 5);
            }
        }

        &.ant-picker-disabled {
            background-color: transparent;
            color: ${DISABLED_COLOR};
            border-color: ${DISABLED_BORDER_COLOR_BOX};

            & .ant-picker-input > input[disabled] {
                cursor: not-allowed;
                color: ${DISABLED_COLOR};
            }

            & .ant-picker-input > input::placeholder {
                color: ${DISABLED_COLOR};
            }
        }

        & .ant-picker-range-separator {
            padding: 0 4px;
        }
    }
    & .ant-picker-active-bar {
        display: none;
    }

    & .ant-picker-status-error.ant-picker,
    & .ant-picker-status-error.ant-picker:not([disabled]),
    & .ant-picker-status-error.ant-picker:not(.ant-picker-disabled) {
        border: 2px solid rgb(224, 0, 0) !important;

        &:not(.ant-picker-disabled):hover,
        &:not(.ant-picker-disabled).ant-picker-focused {
            border-right-width: 2px;
            box-shadow: none;
        }

        &:not(.ant-picker-disabled):hover {
            border-color: rgb(149, 18, 25) !important;
        }

        &:not(.ant-picker-disabled).ant-picker-focused {
            border-color: rgb(1, 2, 5) !important;
        }
    }

    @media screen and (min-width: ${breakpoints.md}) {
        .ant-picker-date-range-wrapper {
            min-width: 300px;
        }
    }
`;

export const RangePickerContainer = styled.div<{ disabled?: boolean }>`
    ${commonPicker}

    ${({ disabled }) => disabled && formItemDisabledCss}
`;

export const FixedRangePickerContainer = styled.div<{ disabled?: boolean }>`
    & .ant-picker:first-child {
        border-radius: 4px 0 0 4px !important;
        border-right: 0 !important;
    }

    & .ant-picker:last-child {
        border-radius: 0 4px 4px 0 !important;
        border-left: 0 !important;
    }

    ${commonPicker}

    ${({ disabled }) => disabled && formItemDisabledCss}
`;

export const DatePickerContainer = styled.div<{ disabled?: boolean }>`
    ${commonPicker};

    ${({ disabled }) => disabled && formItemDisabledCss}
`;

export const UploaderContainer = styled.div`
    ${formItemCss}
`;

export default {
    RangePickerContainer,
    FixedRangePickerContainer,
    DatePickerContainer,
};

export const StyledTextFieldWrapper = styled(PTextFieldWrapper)`
    width: 100%;
`;

export const ReplicateInputField = styled.div<{ disabled?: boolean; readOnly?: boolean }>`
    font-family: ${themeTokens.fontFamily};
    display: grid;
    gap: 4px;

    --p-internal-text-field-input-padding-start: 16px;
    --p-internal-text-field-input-padding-end: 16px;

    & > .label {
        font-size: ${themeTokens.baseFontSize};
        line-height: ${themeTokens.fontLineHeight};
        color: ${themeTokens.textColor};
        hyphens: auto;

        ${({ disabled }) =>
            disabled &&
            `
            color: ${DISABLED_COLOR};
            cursor: not-allowed;
        `}
    }

    & > div {
        display: block;
        font-size: ${themeTokens.inputFontSize};
        line-height: 34px;
        width: 100%;
        height: calc(calc(6px + 2.125ex) + 10px + 2px * 2 + 8px * 2);
        margin: 0;
        outline: 0;
        appearance: none;
        border: 2px solid rgb(107, 109, 112);
        border-radius: 4px;
        background-color: transparent;
        text-indent: 0;
        padding: 8px 16px;
        padding-inline-start: var(--p-internal-text-field-input-padding-start);
        padding-inline-end: var(--p-internal-text-field-input-padding-end);

        ${({ disabled }) =>
            disabled &&
            `
            color: ${DISABLED_COLOR};
            border-color: ${DISABLED_BORDER_COLOR_BOX};
            cursor: not-allowed;
        `}

        ${({ readOnly }) =>
            readOnly &&
            `
            background-color: #D8D8DB;
            border-color: #D8D8DB !important;
        `}
    }
`;
